import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SearchForm from '../components/features/SearchForm';
import ResultCard from '../components/features/ResultCard';
import Alert from '../components/ui/Alert';
import Card from '../components/ui/Card';
import WhatsAppButton from '../components/ui/WhatsAppButton';
import InactiveCustomerAlert from '../components/ui/InactiveCustomerAlert';
import sistematizacionService from '../services/sistematizacionService';
import { SistematizacionResponse, ServicesStatusResponse } from '../types/sistematizacion.types';
import { validateCustomerStatus, shouldShowInactiveAlert } from '../utils/customerValidation';
import './HomePage.css';

const HomePage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SistematizacionResponse[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [showInactiveAlert, setShowInactiveAlert] = useState(false);
  const [customerStatus, setCustomerStatus] = useState<{
    reason: 'inactive' | 'frozen' | null;
    customerName: string | null;
    cardCode: string | null;
  }>({
    reason: null,
    customerName: null,
    cardCode: null,
  });

  const [servicesStatus, setServicesStatus] = useState<ServicesStatusResponse | null>(null);
  const [checkingServices, setCheckingServices] = useState(true);

  const checkServices = async () => {
    setCheckingServices(true);
    try {
      const response = await sistematizacionService.checkServicesStatus();
      if (response.Success && response.Data) {
        setServicesStatus(response.Data);
      } else {
        setServicesStatus({
          AllServicesUp: false,
          DatabaseUp: false,
          SapB1Up: false,
          UserMessage: 'No se puede verificar el estado de los servicios. Por favor, intente más tarde.',
          Timestamp: new Date().toISOString(),
        });
      }
    } catch {
      setServicesStatus({
        AllServicesUp: false,
        DatabaseUp: false,
        SapB1Up: false,
        UserMessage: 'No se puede conectar al servidor. Por favor, intente más tarde.',
        Timestamp: new Date().toISOString(),
      });
    } finally {
      setCheckingServices(false);
    }
  };

  useEffect(() => {
    checkServices();
    // Re-verificar cada 3 minutos por si el servicio vuelve a estar disponible
    const interval = setInterval(checkServices, 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleSearch = async (placa: string, captchaToken: string, resetCaptcha: () => void) => {
    setLoading(true);
    setError(null);
    setShowResults(false);
    setShowInactiveAlert(false);

    try {
      const response = await sistematizacionService.consultarPorPlacaGet(placa, captchaToken);

      if (response.Success && response.Data) {
        // Validar estado del cliente
        const status = validateCustomerStatus(response.Data);

        if (shouldShowInactiveAlert(status)) {
          // Cliente inactivo o congelado - mostrar alerta
          setCustomerStatus({
            reason: status.reason,
            customerName: response.Data[0]?.CardName || null,
            cardCode: response.Data[0]?.CardCode || null,
          });
          setShowInactiveAlert(true);
          console.warn('Cliente inactivo detectado:', status.message);
        }

        // Mostrar resultados independientemente del estado del cliente
        setResults(response.Data);
        setShowResults(true);
      } else {
        setError(response.Message || 'No se encontraron resultados');
        setResults(null);
      }
    } catch (err: any) {
      console.error('Error al consultar:', err);

      const statusCode = err.response?.status;
      if (statusCode === 403 || statusCode === 400) {
        setError(err.response?.data?.Message || 'Captcha inválido. Por favor, intente nuevamente.');
      } else {
      setError(
        err.response?.data?.Message ||
        'Error al conectar con el servidor. Por favor, intente nuevamente.'
      );
      }
      setResults(null);
    } finally {
      resetCaptcha();
      setLoading(false);
    }
  };

  return (
    <div className="app">
      <Header />
      
      <main className="main-content">
        <div className="container">
          <section className="hero-section">
            <h1 className="hero-title">
              <span style={{ filter: 'grayscale(0)' }}>🚗</span> Plataforma de Sistematización
            </h1>
            <p className="hero-subtitle">
              Consulte el estado de sistematización de su vehículo
            </p>
          </section>

          <section className="search-section">
            <Card>
              <h2 className="section-title">Consultar Sistematización</h2>
              {checkingServices && (
                <p className="services-checking-msg">Verificando disponibilidad del servicio...</p>
              )}
              {!checkingServices && servicesStatus && !servicesStatus.AllServicesUp && (
                <Alert
                  type="error"
                  message={servicesStatus.UserMessage || 'El servicio no está disponible en este momento. Por favor, intente más tarde.'}
                />
              )}
              <SearchForm
                onSearch={handleSearch}
                loading={loading}
                disabled={checkingServices || (servicesStatus !== null && !servicesStatus.AllServicesUp)}
              />
            </Card>
          </section>

          {error && (
            <section className="alert-section">
              <Alert type="error" message={error} onClose={() => setError(null)} />
            </section>
          )}

          {showInactiveAlert && customerStatus.reason && (
            <section className="alert-section">
              <InactiveCustomerAlert
                customerName={customerStatus.customerName}
                cardCode={customerStatus.cardCode}
                reason={customerStatus.reason}
                onClose={() => setShowInactiveAlert(false)}
              />
            </section>
          )}

          {showResults && results && (
            <section className="results-section">
              <h2 className="section-title">📋 Resultados</h2>
              <ResultCard data={results} />
            </section>
          )}

          {!showResults && !error && !loading && (
            <section className="info-section">
              <Card variant="info">
                <h3>ℹ️ Información</h3>
                <p>
                  Ingrese la placa de su vehículo para consultar el estado de sistematización.
                  El sistema le mostrará:
                </p>
                <ul>
                  <li>Tipo de vehículo</li>
                  <li>Último año pagado</li>
                  <li>Deudas pendientes (si las hay)</li>
                  <li>Valor total a pagar</li>
                </ul>
              </Card>
            </section>
          )}
        </div>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default HomePage;
